import DOMPurify from 'isomorphic-dompurify';

/**
 * XSS Protection Utilities
 * Sanitize user inputs to prevent XSS attacks
 */

/**
 * Sanitize any user input
 * @param {any} input - User input (string, number, object, array)
 * @param {Object} options - Sanitization options
 * @returns {any} - Sanitized input
 */
export const sanitize = (input, options = {}) => {
  if (input === null || input === undefined) {
    return input;
  }

  // String sanitization
  if (typeof input === 'string') {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: options.allowHTML ? ['b', 'i', 'em', 'strong', 'a', 'p', 'br'] : [],
      ALLOWED_ATTR: options.allowHTML ? ['href', 'title'] : [],
      KEEP_CONTENT: true,
      ...options
    }).trim();
  }

  // Number sanitization
  if (typeof input === 'number') {
    return isNaN(input) ? null : input;
  }

  // Array sanitization
  if (Array.isArray(input)) {
    return input.map(item => sanitize(item, options));
  }

  // Object sanitization
  if (typeof input === 'object') {
    const sanitized = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        sanitized[key] = sanitize(input[key], options);
      }
    }
    return sanitized;
  }

  return input;
};

/**
 * Sanitize text input (no HTML allowed)
 * @param {string} input - Text input
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // First, use DOMPurify to remove all HTML tags and dangerous content
  let sanitized = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'style', 'meta'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  });
  
  // Remove any remaining dangerous patterns
  sanitized = sanitized
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/<iframe/gi, '') // Remove iframe tags
    .replace(/<object/gi, '') // Remove object tags
    .replace(/<embed/gi, '') // Remove embed tags
    .replace(/data:text\/html/gi, '') // Remove data URIs with HTML
    .trim();
  
  return sanitized;
};

/**
 * Sanitize HTML content (limited HTML allowed)
 * @param {string} input - HTML input
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHTML = (input) => {
  if (!input || typeof input !== 'string') return '';
  return sanitize(input, { allowHTML: true });
};

/**
 * Sanitize email input
 * @param {string} input - Email input
 * @returns {string} - Sanitized email
 */
export const sanitizeEmail = (input) => {
  if (!input || typeof input !== 'string') return '';
  return sanitizeText(input)
    .toLowerCase()
    .trim()
    .replace(/[<>\"'&]/g, '');
};

/**
 * Sanitize number input
 * @param {string|number} input - Number input
 * @returns {number|null} - Sanitized number
 */
export const sanitizeNumber = (input) => {
  if (typeof input === 'number') {
    return isNaN(input) ? null : input;
  }
  if (typeof input === 'string') {
    const cleaned = input.replace(/[^0-9.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }
  return null;
};

/**
 * Sanitize URL input
 * @param {string} input - URL input
 * @returns {string} - Sanitized URL
 */
export const sanitizeURL = (input) => {
  if (!input || typeof input !== 'string') return '';
  return sanitizeText(input).trim();
};

/**
 * Sanitize form data object
 * @param {Object} formData - Form data object
 * @returns {Object} - Sanitized form data
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') return {};
  
  const sanitized = {};
  for (const key in formData) {
    if (Object.prototype.hasOwnProperty.call(formData, key)) {
      const value = formData[key];
      
      // Special handling for email fields
      if (key.toLowerCase().includes('email')) {
        sanitized[key] = sanitizeEmail(value);
      }
      // Special handling for number fields
      else if (key.toLowerCase().includes('amount') || 
               key.toLowerCase().includes('price') || 
               key.toLowerCase().includes('rate') ||
               key.toLowerCase().includes('roi') ||
               key.toLowerCase().includes('balance')) {
        sanitized[key] = sanitizeNumber(value);
      }
      // Default to text sanitization
      else if (typeof value === 'string') {
        sanitized[key] = sanitizeText(value);
      }
      else {
        sanitized[key] = value;
      }
    }
  }
  
  return sanitized;
};

export default {
  sanitize,
  sanitizeText,
  sanitizeHTML,
  sanitizeEmail,
  sanitizeNumber,
  sanitizeURL,
  sanitizeFormData
};
